import { FromFirestoreConverterFunction, ToFirestoreConverterFunction } from 'common/types';
import {
  collection,
  collectionGroup,
  CollectionReference,
  doc,
  DocumentData,
  Firestore,
  FirestoreDataConverter,
  getDoc,
  getDocs,
  query,
  Query,
  QueryConstraint,
  QueryDocumentSnapshot
} from 'firebase/firestore';
import { FirebaseService } from './base-service';

/**
 * Firestore Service is singleton class to handle Firebase Firestore
 */
export class FirestoreService {

  private static _instance: FirestoreService;
  private readonly baseServiceInstance: FirebaseService;
  private readonly _firestore: Firestore;

  private constructor() {
    this.baseServiceInstance = FirebaseService.instance;
    this._firestore = this
      .baseServiceInstance
      .firestore;
  }

  /**
   * Get the Firestore Singleton service instance
   */
  public static get instance() {
    if (!this._instance) {
      this._instance = new FirestoreService();
    }

    return this._instance;
  }

  /**
   * Get Firestore service
   */
  public get firestore() {
    return this._firestore;
  }

  /**
   * Get collection by name
   * @param nameOfPath Collection name
   * @returns
   */
  public getCollection<T = DocumentData>(nameOfPath: string, withDataConverter?: FirestoreDataConverter<T>) {
    if (withDataConverter) {
      return collection(this.firestore, nameOfPath).withConverter(withDataConverter);
    } else {
      return collection(this.firestore, nameOfPath) as CollectionReference<T>;
    }
  }

  /**
   * Get collection group by name
   * @param nameOfPath Collection group name
   * @param withDataConverter Data converter
   * @returns
   */
  public getCollectionGroup<T = DocumentData>(nameOfPath: string, withDataConverter?: FirestoreDataConverter<T>) {
    if (withDataConverter) {
      return collectionGroup(this._firestore, nameOfPath).withConverter(withDataConverter);
    } else {
      return collectionGroup(this._firestore, nameOfPath) as Query<T>;
    }
  }

  /**
   * Get all query snapshots from collection reference
   * @param collection Collection reference
   * @returns
   */
  public getAllDocuments<T = DocumentData>(collection: Query<T>) {
    return getDocs<T>(collection);
  }

  /**
   * Query documents from collection by constraint (where clauses)
   * @param collection Collection reference
   * @param whereClauses Conditions to query
   * @returns
   */
  public queryDocuments<T = DocumentData>(collection: Query<T>, ...whereClauses: QueryConstraint[]) {
    return query<T>(collection, ...whereClauses);
  }

  /**
   * Create document reference instance from collection by document id
   * @param collection Collection reference
   * @param collectionName
   * @param documentPath
   * @returns
   */
  public createDoc<T = DocumentData>(collection: CollectionReference<T>, documentPath?: string, withDataConverter?: FirestoreDataConverter<T>) {
    if (withDataConverter) {
      return doc<T>(collection, documentPath).withConverter(withDataConverter);
    } else {
      return doc<T>(collection, documentPath);
    }
  }

  /**
   * Get document snapshot from collection by document id
   * @param collection Collection reference
   * @param documentId Document ID
   * @returns
   */
  public getDocument<T = DocumentData>(collection: CollectionReference<T>, documentId: string) {
    const doc = this.createDoc(collection, documentId);
    return getDoc<T>(doc);
  }

  /**
   * Create a Firestore DataConverter
   * @param toFirestoreFn To Firestore converter function
   * @param fromFirestoreFn From Firestore converter function
   * @returns
   */
  public createDataConverter<T = DocumentData>(toFirestoreFn: ToFirestoreConverterFunction<T>, fromFirestoreFn?: FromFirestoreConverterFunction<T>): FirestoreDataConverter<T> {

    fromFirestoreFn = fromFirestoreFn || (
      (doc: QueryDocumentSnapshot<T>) => doc.data()
    );

    return { toFirestore: toFirestoreFn, fromFirestore: fromFirestoreFn };
  }

}
